export type SignupPlan = "pro";
export type SignupBilling = "monthly";

export type SignupIntent = {
  plan: SignupPlan;
  billing: SignupBilling;
};

const pendingCheckoutKey = "mintstint.pendingCheckoutIntent";

function firstParam(value: string | string[] | null | undefined) {
  if (Array.isArray(value)) return value[0];
  return value;
}

export function normalizeSignupIntent(input: {
  plan?: string | string[] | null;
  billing?: string | string[] | null;
}): SignupIntent {
  return { plan: "pro", billing: "monthly" };
}

export function normalizeCheckoutIntent(input: {
  checkout?: string | string[] | null;
  billing?: string | string[] | null;
}): SignupIntent | null {
  const checkout = firstParam(input.checkout);
  if (checkout !== "pro") return null;
  return { plan: "pro", billing: "monthly" };
}

export function getCheckoutPath(
  billing: SignupBilling,
  plan: SignupPlan = "pro",
) {
  // Dedicated auth-style payment screen (not the app dashboard/settings).
  return `/subscribe?plan=${plan}&billing=${billing}`;
}

export function getRegisterPath(intent: SignupIntent) {
  return `/register?plan=${intent.plan}&billing=${intent.billing}`;
}

export function getCheckoutIntentFromPath(
  path: string | string[] | null | undefined,
): SignupIntent | null {
  const value = firstParam(path);
  if (!value?.startsWith("/") || value.startsWith("//")) return null;
  try {
    const url = new URL(value, "https://mintstint.local");
    if (
      url.pathname === "/subscribe" ||
      url.pathname.startsWith("/subscribe")
    ) {
      return normalizeSignupIntent({
        plan: url.searchParams.get("plan"),
        billing: url.searchParams.get("billing"),
      });
    }
    return normalizeCheckoutIntent({
      checkout: url.searchParams.get("checkout"),
      billing: url.searchParams.get("billing"),
    });
  } catch {
    return null;
  }
}

function storage() {
  try {
    return typeof globalThis.localStorage === "undefined"
      ? null
      : globalThis.localStorage;
  } catch {
    return null;
  }
}

export function savePendingCheckoutIntent(intent: SignupIntent) {
  storage()?.setItem(pendingCheckoutKey, JSON.stringify(intent));
}

export function readPendingCheckoutIntent(): SignupIntent | null {
  const raw = storage()?.getItem(pendingCheckoutKey);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<SignupIntent>;
    return parsed.plan === "pro"
      ? {
          plan: "pro",
          billing: "monthly",
        }
      : null;
  } catch {
    return null;
  }
}

export function clearPendingCheckoutIntent() {
  storage()?.removeItem(pendingCheckoutKey);
}
